import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSeoMeta } from "@unhead/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNostr } from "@nostrify/react";
import type { NostrEvent } from "@nostrify/nostrify";
import { ArrowLeft, Inbox, Loader2, Moon, Sun } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LoginArea } from "@/components/auth/LoginArea";
import { ChamaWordmark } from "@/components/chama/ChamaLogo";
import { FoundCommunity } from "@/components/chama/moderate/FoundCommunity";
import { RequestCard } from "@/components/chama/moderate/RequestCard";
import { MembersAdmin } from "@/components/chama/moderate/MembersAdmin";
import { ChannelsAdmin } from "@/components/chama/moderate/ChannelsAdmin";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useChama, communityRelays } from "@/hooks/useChama";
import { useControlPlane } from "@/hooks/useControlPlane";
import { useJoinRequests, useModerationActions } from "@/hooks/useModeration";
import { useMembers } from "@/hooks/useMembers";
import { useTheme } from "@/hooks/useTheme";
import { useToast } from "@/hooks/useToast";
import { APP_NAME, MODERATOR_PUBKEY } from "@/lib/concord/config";
import { publishBanlist, publishCreateChannel } from "@/lib/concord/ops";

export default function ModeratePage() {
  useSeoMeta({ title: `${APP_NAME} — Moderation` });
  const { user } = useCurrentUser();
  const chama = useChama();
  const navigate = useNavigate();
  const { nostr } = useNostr();
  const { theme, setTheme } = useTheme();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const membership = chama.membership;
  const { control, isLoading: controlLoading } = useControlPlane(membership);
  const { requests, isLoading: requestsLoading, markReviewed } = useJoinRequests();
  const { approve, decline, invalidateControl } = useModerationActions(membership);
  const { members } = useMembers(membership, control);

  const isModerator = user?.pubkey === MODERATOR_PUBKEY;

  useEffect(() => {
    if (user && !isModerator) navigate("/");
  }, [user, isModerator, navigate]);

  const sink = {
    event: (e: NostrEvent) =>
      nostr
        .group(communityRelays(membership))
        .event(e, { signal: AbortSignal.timeout(8000) }),
  };

  const ban = useMutation({
    mutationFn: async (pubkey: string) => {
      if (!user || !membership) throw new Error("Not ready");
      await publishBanlist({
        signer: user.signer,
        authorPub: user.pubkey,
        membership,
        banned: [...control.banlist, pubkey],
        heads: control.heads,
        sink,
      });
    },
    onSuccess: () => {
      toast({ title: "Member banned" });
      invalidateControl();
      void queryClient.invalidateQueries({ queryKey: ["chama", "guestbook"] });
    },
    onError: (e) => {
      console.error(e);
      toast({ title: "Ban failed", variant: "destructive" });
    },
  });

  const unban = useMutation({
    mutationFn: async (pubkey: string) => {
      if (!user || !membership) throw new Error("Not ready");
      await publishBanlist({
        signer: user.signer,
        authorPub: user.pubkey,
        membership,
        banned: [...control.banlist].filter((p) => p !== pubkey),
        heads: control.heads,
        sink,
      });
    },
    onSuccess: () => {
      toast({ title: "Member unbanned" });
      invalidateControl();
    },
    onError: (e) => {
      console.error(e);
      toast({ title: "Unban failed", variant: "destructive" });
    },
  });

  const createChannel = useMutation({
    mutationFn: async (name: string) => {
      if (!user || !membership) throw new Error("Not ready");
      await publishCreateChannel({
        signer: user.signer,
        authorPub: user.pubkey,
        membership,
        name,
        heads: control.heads,
        sink,
      });
    },
    onSuccess: () => {
      toast({ title: "Channel created" });
      invalidateControl();
    },
    onError: (e) => {
      console.error(e);
      toast({ title: "Couldn't create the channel", variant: "destructive" });
    },
  });

  if (!user || !isModerator) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const pendingRequests = requests.filter((r) => !r.reviewed);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur">
        <div className="container flex h-16 items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" asChild aria-label="Back">
              <Link to={membership ? "/chat" : "/"}>
                <ArrowLeft className="size-5" />
              </Link>
            </Button>
            <ChamaWordmark />
            <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
              Moderation
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Toggle theme"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            >
              {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </Button>
            <LoginArea className="max-w-40" />
          </div>
        </div>
      </header>

      <main className="container flex-1 py-8 max-w-3xl">
        {chama.status === "loading" || (membership && controlLoading) ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : !membership ? (
          <FoundCommunity
            membership={membership}
            communityList={chama.communityList}
            onFounded={chama.refetchAll}
          />
        ) : (
          <Tabs defaultValue="applications">
            <TabsList className="w-full sm:w-auto">
              <TabsTrigger value="applications" className="gap-2">
                <Inbox className="size-4" />
                Applications
                {pendingRequests.length > 0 && (
                  <span className="rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground">
                    {pendingRequests.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="members">Members</TabsTrigger>
              <TabsTrigger value="channels">Channels</TabsTrigger>
            </TabsList>

            <TabsContent value="applications" className="mt-6 space-y-4">
              {requestsLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-48 w-full" />
                  <Skeleton className="h-48 w-full" />
                </div>
              ) : requests.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-12 px-8 text-center">
                    <p className="text-muted-foreground max-w-sm mx-auto">
                      No applications yet. When someone asks to join, their
                      request lands here — end-to-end encrypted, signed by
                      their key.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Approving sends a Concord Direct Invite to their inbox — it
                    opens here and in Armada.
                  </p>
                  {requests.map((r) => (
                    <RequestCard
                      key={r.wrapId}
                      request={r}
                      approving={approve.isPending}
                      declining={decline.isPending}
                      onApprove={() =>
                        approve.mutate(r.applicant, {
                          onSuccess: () => markReviewed(r.applicant, "approved"),
                        })
                      }
                      onDecline={(message) =>
                        decline.mutate(
                          { applicantPub: r.applicant, message },
                          { onSuccess: () => markReviewed(r.applicant, "declined") },
                        )
                      }
                    />
                  ))}
                </>
              )}
            </TabsContent>

            <TabsContent value="members" className="mt-6">
              <MembersAdmin
                members={members}
                banlist={control.banlist}
                myPub={user.pubkey}
                pending={unban.isPending}
                onBan={(pk) => ban.mutate(pk)}
                onUnban={(pk) => unban.mutate(pk)}
              />
            </TabsContent>

            <TabsContent value="channels" className="mt-6">
              <ChannelsAdmin
                channels={control.channels}
                creating={createChannel.isPending}
                onCreate={(name) => createChannel.mutate(name)}
              />
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
